export type ContractType = 'Volume' | 'Price' | 'BaisseClause';

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface BusinessUnit {
  id: number;
  name: string;
  code: string;
  location: string;
}

// User Management Types (aligned with backend UserViewModel, CreateUserViewModel, UpdateUserViewModel)
export interface User {
  id: number;
  userName: string;
  email: string;
  roles: string[];
  businessUnitId?: number;
  businessUnitName?: string;
}

export interface CreateUserPayload {
  email: string;
  role: string;
  businessUnitId?: number;
}

export interface UpdateUserPayload {
  id: number;
  userName: string;
  email: string;
  role: string;
  businessUnitId?: number;
}

export interface EmailAvailabilityResponse {
  email: string;
  isAvailable: boolean;
}

export interface UserProfileResponse {
  id?: number;
  userName: string;
  email: string;
  roles: string[];
  isAuthenticated: boolean;
}

// Role Management Types (aligned with backend RoleViewModel, CreateRoleViewModel)
export interface Role {
  id: number;
  name: string;
}

export interface CreateRolePayload {
  name: string;
}

export interface RoleNameAvailabilityResponse {
  name: string;
  isAvailable: boolean;
}

export interface VolumeDistribution {
  id: number;
  businessUnitId: number;
  businessUnitName: string;
  productName: string;
  supplierName: string; // Can be local entity/distributor
  volumeKgs: number;
  incoTerm: string;
  incoTermPlace: string;
  pricePerKg: number;
  currency: string;
  shippingInstructions: string;
  confirmedBy?: string;
}

export interface Contract {
  id: number;
  contractName: string;
  contractDate: Date;
  supplierName: string;
  productName: string;
  contractType: ContractType;
  startDate: Date;
  endDate: Date;
  totalVolume: number;
  businessUnitMode: 'Single' | 'Several';
  // Fields only used when businessUnitMode is 'Single' (for backward compatibility)
  businessUnitId?: number;
  businessUnitName?: string;
  incoTerm?: string;
  incoTermPlace?: string;
  pricePerKg?: number;
  currency?: string;
  confirmedBy?: string;
  shippingInstructions?: string;
  // Volume distributions - NOW ALWAYS POPULATED for both Single and Several modes
  volumeDistributions: VolumeDistribution[];
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

// Query Key Constants for React Query
export const QUERY_KEYS = {
  users: ['users'] as const,
  user: (id: number) => ['user', id] as const,
  userEmailAvailability: (email: string) => ['userEmailAvailability', email] as const,
  roles: ['roles'] as const,
  role: (id: number) => ['role', id] as const,
  roleNameAvailability: (name: string) => ['roleNameAvailability', name] as const,
  currentUser: ['currentUser'] as const,
  contracts: ['contracts'] as const,
  contract: (id: number) => ['contracts', id] as const,
  businessUnits: ['businessUnits'] as const,
  businessUnit: (id: number) => ['businessUnits', id] as const,
} as const;
